import * as signalR from "@microsoft/signalr";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Common from "../based/Common";
import { INITPAGING, NOTIFY } from "../based/Constants";
import { Notify } from "../based/Notify";
import GameServices from "./GameServices";
import connection from "./SignalR";

const TeamType = {
    HOME: "home",
    AWAY: "away",
};

const HockeyTeam = {
    NHL: 0,
    KHL: 1,
};

function NHLGameMainPage(props) {
    const { hockeyTeam } = props.match.params;

    const [paging, setPaging] = useState({ ...INITPAGING });
    const [listModel, setListModel] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef(null);

    useEffect(() => {
        let page = { ...paging };
        if (hockeyTeam == null) {
            page.hockeyTeam = HockeyTeam.NHL;
        } else {
            page.hockeyTeam = +hockeyTeam;
        }
        getData(page);
    }, []);

    useEffect(() => {
        if (!connection) return;

        //chưa connection thì connect
        if (connection.state === signalR.HubConnectionState.Disconnected) {
            connection
                .start()
                .then(() => {})
                .catch((err) => console.error(`NHLGameMainPage: ${err}`));
        }

        //handler nhận message
        const receiveHandler = (gameStatus) => {
            if (!gameStatus) return;
            const { gameId, isPlaying } = gameStatus;
            console.log("receiveHandler :>> ", gameStatus);
            setListModel((prevList) => {
                return prevList.map((item) => (item.nhlGameId == gameId ? { ...item, isPlaying } : item));
            });
        };

        connection.on("ReceiveGameStatus", receiveHandler);
        return () => {
            connection.off("ReceiveGameStatus", receiveHandler);
        };
    }, []);

    //lấy ds game
    const getData = async (page) => {
        setLoading(true);
        const [err, data] = await GameServices.GetGameClientByPage(page);
        if (!err && data && data.listObjects && data.listObjects.length > 0) {
            if (page.pageNumber == 1) {
                let dt = data.listObjects;
                dt = dt.sort((a, b) => {
                    // Sort ngày: mới -> cũ
                    const dateCompare = new Date(b.date) - new Date(a.date);
                    if (dateCompare !== 0) return dateCompare;

                    // Nếu cùng ngày: time nhỏ -> lớn
                    return a.time.localeCompare(b.time);
                });
                setListModel(dt);
            } else {
                let dt = [...listModel, ...data.listObjects];
                dt = dt.sort((a, b) => {
                    // Sort ngày: mới -> cũ
                    const dateCompare = new Date(b.date) - new Date(a.date);
                    if (dateCompare !== 0) return dateCompare;

                    // Nếu cùng ngày: time nhỏ -> lớn
                    return a.time.localeCompare(b.time);
                });
                setListModel(dt);
            }
            setPaging(data.paging);
            setHasMore(true);
        } else {
            if (page.pageNumber == 1) setListModel([]);
            setHasMore(false);
            setPaging(page);
        }

        setLoading(false);
    };

    const setLastCollectionHandler = (element) => {
        if (loading || !hasMore) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                let updatePaging = { ...paging };
                updatePaging.pageNumber += 1;
                getData(updatePaging);
            }
        });
        if (element) observer.current.observe(element);
    };

    return (
        <Wrapper>
            <div className="lst_game">
                {listModel && listModel.length > 0 ? (
                    listModel.map((item, index) => {
                        return <GameItem item={item} index={index} setLastCollectionHandler={setLastCollectionHandler} key={index} />;
                    })
                ) : (
                    <div className="no-data">
                        <span>No data available</span>
                    </div>
                )}
            </div>
        </Wrapper>
    );
}

export default NHLGameMainPage;

function GameItem({ item, index, setLastCollectionHandler }) {
    const [deltaModels, setDeltaModels] = useState([]);
    const [gamePeriodScores, setGamePeriodScores] = useState([]);
    const [lastEvent, setLastEvent] = useState(null);
    const [filterPeriod, setFilterPeriod] = useState(0);

    const lastPeriod = useMemo(() => {
        let lp = 0;
        if (deltaModels && deltaModels.length > 0 && item.isPlaying) {
            const lastDelta = deltaModels[deltaModels.length - 1];
            if (lastDelta && lastDelta.period) {
                lp = lastDelta.period;
            }
        }
        return lp;
    }, [deltaModels, item.isPlaying]);

    const deltaGasGroup = useMemo(() => {
        if (!deltaModels || deltaModels.length == 0) return;

        //group lại theo period
        const listPeriod = [1, 2, 3];
        const listItem = [];

        for (const element of listPeriod) {
            var homeGas = deltaModels.reduce((prev, cur, i) => (cur.period == element ? (prev += cur.gasHome) : prev), 0);
            var awayGas = deltaModels.reduce((prev, cur, i) => (cur.period == element ? (prev += cur.gasAway) : prev), 0);
            var homeItem = {
                period: element,
                homeGas: homeGas.toFixed(0),
                awayGas: awayGas.toFixed(0),
            };
            listItem.push(homeItem);
        }

        return listItem;
    }, [deltaModels]);

    const deltaGasByPeriod = useCallback(
        (period) => {
            if (!deltaGasGroup || deltaGasGroup.length == 0) return {};
            var item = deltaGasGroup.find((x) => x.period == period);
            return item || {};
        },
        [deltaGasGroup]
    );

    useEffect(() => {
        //config score từng hiệp
        setGamePeriodScores(item.gamePeriodScores);
    }, [item]);

    useEffect(() => {
        let intervalId;
        if (item.isPlaying) {
            //start now
            directGetDeltaList(item.nhlGameId);

            //interval mỗi 10s
            intervalId = setInterval(() => {
                directGetDeltaList(item.nhlGameId);
            }, 10000);
        } else {
            //lấy thông tin delta theo event
            getDeltaListByGameId(item.nhlGameId);
        }
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [item.isPlaying]);

    async function directGetDeltaList(nhlGameId) {
        await directGetDeltaListByGameId(nhlGameId);

        //scroll to end
        scrollRight(nhlGameId);
    }

    const scrollRight = (nhlGameId) => {
        let documentId = `lst_delta-gas-${nhlGameId}`;
        let element = document.getElementById(documentId);
        if (element) {
            element.scrollLeft = element.scrollWidth;
        }
    };

    const directGetDeltaListByGameId = async (nhlGameId) => {
        const [err, data] = await GameServices.DirectGetDeltaListByGameId(nhlGameId);
        if (!err && data && data.data && data.data.length > 0) {
            setDeltaModels(data.data);
            setLastEvent(data.lastEvent);
            setGamePeriodScores(data.periodScores);
        } else {
            setDeltaModels([]);
            setLastEvent(null);
        }
    };

    const getDeltaListByGameId = async (nhlGameId) => {
        const [err, data] = await GameServices.GetDeltaListByGameId(nhlGameId);
        if (!err && data && data.data && data.data.length > 0) {
            setDeltaModels(data.data);
            scrollRight(nhlGameId);
            setLastEvent(null);
        } else {
            setDeltaModels([]);
            setLastEvent(null);
        }
    };

    const _getScoreByPeriod = (scores, period, type = TeamType.HOME) => {
        if (scores && scores.length > 0) {
            const scoreObj = scores.find((s) => s.period == period);
            if (scoreObj) {
                return type === "home" ? scoreObj.home : scoreObj.away;
            }
        }
        return "-";
    };

    const _getTotalScore = (scores, type = TeamType.HOME) => {
        if (scores && scores.length > 0) {
            let total = 0;
            scores.forEach((scoreObj) => {
                total += type === TeamType.HOME ? scoreObj.home : scoreObj.away;
            });
            return total;
        }
        return "-";
    };

    const handleHideDelta = (periodNumber = 0) => {
        let lstDelta = [...deltaModels];
        if (periodNumber > 0) {
            lstDelta = lstDelta.map((dm) => {
                if (dm.period == periodNumber) {
                    dm.isHidden = false;
                } else {
                    dm.isHidden = true;
                }
                return dm;
            });
        } else {
            lstDelta = lstDelta.map((dm) => {
                dm.isHidden = false;
                return dm;
            });
        }
        setDeltaModels(lstDelta);
        setFilterPeriod(periodNumber);
    };

    const exportData = async (nhlGameId) => {
        const [err, data] = await GameServices.HandleIndexByGameId(nhlGameId);
        if (!err && data) {
            window.open(data.url, "_blank");
        } else {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Export failed");
        }
    };

    const trimText = (homeAbbrName) => {
        if (!homeAbbrName || homeAbbrName.length <= 3) return homeAbbrName;

        let slice = homeAbbrName.slice(0, 3);
        return slice;
    };

    return (
        <div className={`game_item ${item.isPlaying ? "current" : ""}`} key={index} ref={setLastCollectionHandler}>
            <div className="game_item-header">
                <div className="game_item-header-left">
                    <div className="time"></div>
                    <div className="date-time">
                        <span>{Common.formatDate(new Date(item.gameDate), "timedate")}</span>
                    </div>
                </div>
                <div className="game_item-header-right">
                    <div
                        className="export-index mx-2 cursor-pointer"
                        onClick={(e) => {
                            exportData(item.nhlGameId);
                        }}
                    >
                        <span className="badge badge-custom">Exp</span>
                    </div>
                    {item.homeRate == 0 && item.awayRate == 0 && item.drawRate == 0 ? null : (
                        <div className="game_rate">
                            <div className="game_rate-left">
                                <span>
                                    <main className={item.lastHomeRate > 0 && item.lastHomeRate != item.homeRate ? (item.lastHomeRate > item.homeRate ? "text-danger" : "text-danger") : ""}>{item.homeRate}</main>
                                </span>
                            </div>
                            <div className="game_rate-mid">
                                <span>
                                    <main className={item.lastDrawRate > 0 && item.lastDrawRate != item.drawRate ? (item.lastDrawRate > item.drawRate ? "text-danger" : "text-danger") : ""}>{item.drawRate}</main>
                                </span>
                            </div>
                            <div className="game_rate-right">
                                <span>
                                    <main className={item.lastAwayRate > 0 && item.lastAwayRate != item.awayRate ? (item.lastAwayRate > item.awayRate ? "text-danger" : "text-danger") : ""}>{item.awayRate}</main>
                                </span>
                            </div>
                        </div>
                    )}

                    <div
                        className="icon cursor-pointer"
                        onClick={(e) => {
                            let url;
                            if (item.hockeyTeam == HockeyTeam.NHL) {
                                url = `https://www.nhl.com/gamecenter/min-vs-nsh/2026/02/04/${item.nhlGameId}`;
                            } else {
                                url = `https://en.khl.ru/game/1369/${item.nhlGameId}/preview/`;
                            }
                            window.open(url, "_blank");
                        }}
                    >
                        <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 13L7 7L1 1" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                </div>
            </div>
            <div className="game_item_body">
                <div className="game_item_body-header">
                    <div className="header-detail">
                        {item.isFinished ? (
                            <span className="txt-orange">FINISH</span>
                        ) : (
                            lastEvent && (
                                <span className="txt-orange">
                                    H{lastEvent.periodNumber} - {lastEvent.typeDescKey == "game-end" ? "END" : lastEvent.totalMinuteInPeriod}
                                </span>
                            )
                        )}
                    </div>
                    <div className="header-pts">
                        <span>PTS</span>
                    </div>
                    <div className="header-dgp">
                        <span>DGP</span>
                    </div>
                    <div className="header-percentage-true">
                        <span>% TRUE</span>
                    </div>
                    <div className={`header-h1 ${lastPeriod == 1 ? "txt-orange" : ""} ${filterPeriod == 1 ? "badge badge-succes" : ""}`} onClick={(e) => handleHideDelta(filterPeriod == 1 ? 0 : 1)}>
                        <span>H1</span>
                    </div>
                    <div className={`header-h2 ${lastPeriod == 2 ? "txt-orange" : ""} ${filterPeriod == 2 ? "badge badge-succes" : ""}`} onClick={(e) => handleHideDelta(filterPeriod == 2 ? 0 : 2)}>
                        <span>H2</span>
                    </div>
                    <div className={`header-h3 ${lastPeriod == 3 ? "txt-orange" : ""} ${filterPeriod == 3 ? "badge badge-succes" : ""}`} onClick={(e) => handleHideDelta(filterPeriod == 3 ? 0 : 3)}>
                        <span>H3</span>
                    </div>
                    <div className={`header-t ${filterPeriod == 0 ? "badge badge-succes" : ""}`} onClick={(e) => handleHideDelta(0)}>
                        <span>T</span>
                    </div>
                </div>
                <div className="game_item_body-body">
                    <div className="body_detail-item">
                        <div className="logo_detail">
                            <div className="icon">
                                <img src={item.homeLogo} alt="" />
                            </div>
                            <div className="name_detail">
                                <div className="name_detail-top">
                                    <div className="team-abbr">
                                        <span>{item.homeAbbrName ? trimText(item.homeAbbrName.toUpperCase()) : ""}</span>
                                    </div>
                                    <div className="badge badge-succes">
                                        <span>{item.homeRank}</span>
                                    </div>
                                    <div className="team_sub">
                                        <span>{item.homeBoard}</span>
                                    </div>
                                </div>
                                <div className="name_detail-bottom">
                                    <span>{item.nhlHomeTeamName}</span>
                                </div>
                            </div>
                        </div>

                        <div className="pts-point">
                            <span>{item.homePts}</span>
                        </div>
                        <div className="dgp-point">
                            <span>{item.homeDeltaGoalPenalty}</span>
                        </div>
                        <div className="percentage-point">
                            <span>{Math.round(item.homePercentageWin * 100)}%</span>
                        </div>
                        <div className="h1-point">
                            <span>{_getScoreByPeriod(gamePeriodScores, 1, TeamType.HOME)}</span>
                            <div className="badge badge-success mx-1">{deltaGasByPeriod(1)?.homeGas}</div>
                        </div>
                        <div className="h2-point">
                            <span>{_getScoreByPeriod(gamePeriodScores, 2, TeamType.HOME)}</span>
                            <div className="badge badge-success mx-1">{deltaGasByPeriod(2)?.homeGas}</div>
                        </div>
                        <div className="h3-point">
                            <span>{_getScoreByPeriod(gamePeriodScores, 3, TeamType.HOME)}</span>
                            <div className="badge badge-success mx-1">{deltaGasByPeriod(3)?.homeGas}</div>
                        </div>
                        <div className="t-point">
                            <span>{_getTotalScore(gamePeriodScores, TeamType.HOME)}</span>
                        </div>
                    </div>
                    <div className="body_detail-item">
                        <div className="logo_detail">
                            <div className="icon">
                                <img src={item.awayLogo} alt="" />
                            </div>
                            <div className="name_detail">
                                <div className="name_detail-top">
                                    <div className="team-abbr">
                                        <span>{item.awayAbbrName ? trimText(item.awayAbbrName.toUpperCase()) : ""}</span>
                                    </div>
                                    <div className="badge badge-danger">
                                        <span>{item.awayRank}</span>
                                    </div>
                                    <div className="team_sub">
                                        <span>{item.awayBoard}</span>
                                    </div>
                                </div>
                                <div className="name_detail-bottom">
                                    <span>{item.nhlAwayTeamName}</span>
                                </div>
                            </div>
                        </div>

                        <div className="pts-point">
                            <span>{item.awayPts}</span>
                        </div>
                        <div className="dgp-point">
                            <span>{item.awayDeltaGoalPenalty}</span>
                        </div>
                        <div className="percentage-point">
                            <span>{Math.round(item.awayPercentageWin * 100)}%</span>
                        </div>
                        <div className="h1-point">
                            <span>{_getScoreByPeriod(gamePeriodScores, 1, TeamType.AWAY)}</span>
                            <div className="badge badge-success mx-1">{deltaGasByPeriod(1)?.awayGas}</div>
                        </div>
                        <div className="h2-point">
                            <span>{_getScoreByPeriod(gamePeriodScores, 2, TeamType.AWAY)}</span>
                            <div className="badge badge-success mx-1">{deltaGasByPeriod(2)?.awayGas}</div>
                        </div>
                        <div className="h3-point">
                            <span>{_getScoreByPeriod(gamePeriodScores, 3, TeamType.AWAY)}</span>
                            <div className="badge badge-success mx-1">{deltaGasByPeriod(3)?.awayGas}</div>
                        </div>
                        <div className="t-point">
                            <span>{_getTotalScore(gamePeriodScores, TeamType.AWAY)}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="game_item-bottom">
                {deltaModels && deltaModels.length > 0 ? (
                    <div className={`lst_delta-gas`} id={`lst_delta-gas-${item.nhlGameId}`}>
                        {deltaModels.map((dm, di) => {
                            if (dm.isHidden) return null;
                            return (
                                <React.Fragment key={di}>
                                    <span className={`delta-gas-item ${dm.deltaGas >= 1.3 ? "success" : dm.deltaGas <= -1.3 ? "danger" : ""}`}>{dm.deltaGas == null ? dm.note.charAt(0) : dm.deltaGas}</span>
                                    {dm.typeCode == 505 && <span className={`${dm.teamType == 1 ? "badge badge-succes" : " badge badge-danger"} mx-1`}>G</span>}
                                </React.Fragment>
                            );
                        })}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

const Wrapper = styled.div`
    background: #8bbaed;
    font-family: "Roboto", sans-serif;

    .h1-point,
    .h2-point,
    .h3-point {
        display: flex;
        flex-direction: column;
    }

    .h1-point .badge,
    .h2-point .badge,
    .h3-point .badge {
        font-size: 9px;
    }

    ///

    .badge-succes {
        color: #ffff !important;
    }

    .lst_delta-gas {
        display: flex;
        align-items: center;
    }

    .badge {
        padding: 2px 4px;
        min-width: auto;
    }

    .icon img {
        width: 46px;
        height: 30px;
        object-fit: contain;
    }

    .logo_detail .icon {
        margin-right: 4px;
    }

    .name_detail-bottom {
        display: flex;
        margin-top: 2px;
    }

    .game_item-header {
        display: flex;
        align-items: center;
        padding: 8px;
    }

    .game_item {
        margin: 12px;
        border-radius: 8px;
        border: 1px solid #eaeaea;
        background: #fff;
    }

    .lst_game {
        display: flex;
        flex-direction: column;
    }

    .game_item-header-right {
        display: flex;
        align-items: center;
        margin-left: auto;
    }

    .game_item.current {
        background: #ffe8cf;
    }

    .game_rate {
        display: flex;
        align-items: center;
        border-radius: 4px;
        background: rgba(0, 0, 0, 0.1);
        padding: 4px 8px;
    }

    .game_item-header-left {
        display: flex;
        align-items: center;
    }

    .game_item-header-left .time span {
        color: #000;
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
    }

    .game_item-header-left .date-time span {
        color: #000;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    .date-time {
        margin-left: 12px;
    }

    .game_rate span {
        display: flex;
        align-items: baseline;
    }

    .game_rate span sub {
        color: #858585;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        bottom: 0 !important;
        margin-right: 4px;
    }

    .game_rate span main {
        color: #000;
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
    }

    .game_rate-left {
        margin: 0 6px;
    }

    .game_rate-mid {
        margin: 0 6px;
    }

    .game_rate-right {
        margin: 0 6px;
    }

    .icon {
        display: flex;
    }

    .game_item-header-right .icon {
        margin-left: 6px;
    }

    .game_item_body-header {
        display: flex;
        align-items: center;
    }

    .body_detail-item {
        display: flex;
        align-items: center;
        margin: 4px 0;
    }

    .logo_detail {
        display: flex;
        align-items: center;
        width: 25%;
        overflow: hidden;
        flex: 1;
    }

    .header-detail {
        width: 25%;
        flex: 1;
    }

    .header-pts {
        width: 10%;
        display: flex;
        justify-content: center;
        color: #77888e;
        text-align: center;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    .header-dgp {
        width: 10%;
        display: flex;
        justify-content: center;
        color: #77888e;
        text-align: center;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    .header-percentage-true {
        width: 15%;
        display: flex;
        justify-content: center;
        color: #77888e;
        text-align: center;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    .header-h1 {
        width: 7.5%;
        display: flex;
        justify-content: center;
        color: #77888e;
        text-align: center;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        cursor: pointer;
    }

    .header-h2 {
        width: 7.5%;
        display: flex;
        justify-content: center;
        color: #77888e;
        text-align: center;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        cursor: pointer;
    }

    .header-h3 {
        width: 7.5%;
        display: flex;
        justify-content: center;
        color: #77888e;
        text-align: center;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        cursor: pointer;
    }

    .header-t {
        width: 10%;
        display: flex;
        justify-content: center;
        color: #77888e;
        text-align: center;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        cursor: pointer;
    }

    .header-detail span.txt-orange {
        color: #ff9100;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
    }

    .game_item_body-header {
        .txt-orange {
            color: #ff9100;
            font-size: 12px;
            font-style: normal;
            font-weight: 700;
            line-height: normal;
        }
    }

    .game_item_body {
        padding: 4px 4px;
        border: 1px solid #eaeaea;
    }

    .name_detail-top {
        display: flex;
        align-items: center;
    }

    .pts-point {
        width: 10%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .dgp-point {
        width: 10%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .percentage-point {
        width: 15%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .h1-point {
        width: 7.5%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .h2-point {
        width: 7.5%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .h3-point {
        width: 7.5%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .t-point {
        width: 10%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .name_detail-bottom span {
        color: #77888e;
        font-size: 10px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    .name_detail-top .team-abbr span {
        color: #000;
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
    }

    .name_detail-top .team_sub {
        color: #77888e;
        font-size: 10px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;

        span {
            display: -webkit-box;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            word-break: break-word;
        }
    }

    .name_detail-top .badge {
        margin: 0 4px;
    }

    span.delta-gas-item {
        color: #000;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        margin: 0 4px;
    }

    span.delta-gas-item.success {
        border-radius: 4px;
        background: #00d500;
        color: white;
        padding: 0 4px;
    }

    span.delta-gas-item.danger {
        border-radius: 4px;
        background: #c8102e;
        color: white;
        padding: 0 4px;
    }

    .lst_delta-gas {
        padding: 4px 0;
        overflow-x: auto;
    }

    .team-abbr {
        display: flex;
        align-items: center;
    }
`;
